import { useIsAdmin } from "@/modules/account/hooks/useIsAdmin";
import Form from "@/components/Form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import CheckboxField from "@/components/CheckboxField";
import { ProjectPlannedDurationVisibilityFields } from "./ProjectPlannedDurationVisibilityFields";
import ClientSelect from "@/components/ClientSelect";
import DatePicker from "@/components/DatePicker";
import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import InvoicingCompanySelect from "@/components/InvoicingCompanySelect";
import PageContent from "@/components/PageContent";
import PayrollTypeSelect from "@/components/PayrollTypeSelect";
import ProjectTypeSelect from "@/components/ProjectTypeSelect";
import SaveButton from "@/components/SaveButton";
import ServicesAndGoodsSelect from "@/components/ServicesAndGoodsSelect";
import { Input } from "@/components/ui/input";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { isRequired } from "@/lib/form";
import { PROJECT_DEPENDENCY_ROLE } from "@/schemas/commonFields";
import { project as projectSchema, writeProject } from "@/schemas/project";
import { ApiError } from "@/services/api";
import { ReadProject, WriteProject } from "@/types/project";
import {
  ReadProjectType,
  ReadProjectTypeServicesAndGoods,
} from "@/types/projectType";
import { UseMutateAsyncFunction } from "@tanstack/react-query";
import { Warehouse } from "lucide-react";
import { useCallback, useState } from "react";
import { ControllerRenderProps, FieldValues, useForm } from "react-hook-form";
import { defaultCreationValues } from "../defaultValues";
import ProjectPrimaryManagerSelect from "./ProjectPrimaryManagerSelect";
import { MIN_PROJECT_DATE } from "@/constants";

interface ProjectCreatorFormProps {
  project?: WriteProject;
  onSubmit: UseMutateAsyncFunction<ReadProject, ApiError, WriteProject>;
  disabled?: boolean;
}

const ProjectCreatorForm = ({
  project = defaultCreationValues,
  onSubmit,
  disabled,
}: ProjectCreatorFormProps) => {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const [projectType, setProjectType] = useState<ReadProjectType | null>(null);

  const [servicesAndGoods, setServicesAndGoods] =
    useState<ReadProjectTypeServicesAndGoods>({});

  const isRoleBasedProject =
    projectType?.project_depends_on === PROJECT_DEPENDENCY_ROLE;

  const form = useForm<WriteProject>({
    resolver: zodResolver(writeProject),
    defaultValues: project,
    disabled,
  });

  const createProjectTypeHandler = useCallback(
    (field: ControllerRenderProps<FieldValues, string>) =>
      (id: string, projectType: ReadProjectType) => {
        setProjectType(projectType);
        const {
          services_and_goods_id,
          services_and_goods_code,
          services_and_goods,
        } = projectType;
        if (projectType.services_and_goods_id) {
          form.setValue("services_and_goods_id", services_and_goods_id);
          setServicesAndGoods({
            services_and_goods_code,
            services_and_goods,
          });
        }
        field.onChange(id);
      },
    [],
  );
  useFormUnsavedChangesWarning(form);

  const handleSubmit = async (values: WriteProject) => {
    await onSubmit(values);
  };

  return (
    <Form form={form} handleSubmit={handleSubmit}>
      <PageContent
        icon={<Warehouse className="w-6 h-6 mr-3" />}
        title={t("projects.title.single")}
        footer={<SaveButton />}
      >
        <Fieldset className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field
            name="name"
            label={t("projects.fields.name")}
            required={isRequired(projectSchema.shape.name)}
            render={({ field }) => <Input className="w-full" {...field} />}
          />
          <Field
            name="primary_manager_id"
            label={t("projects.fields.primary_manager")}
            required={isRequired(projectSchema.shape.primary_manager_id)}
            render={({ field }) => (
              <ProjectPrimaryManagerSelect className="w-full" {...field} />
            )}
          />
          <Field
            name="client_id"
            label={t("projects.fields.client")}
            required={isRequired(projectSchema.shape.client_id)}
            render={({ field }) => (
              <ClientSelect className="w-full" {...field} />
            )}
          />
          <Field
            name="project_type_id"
            label={t("projects.fields.project_type")}
            required={isRequired(projectSchema.shape.project_type_id)}
            render={({ field }) => (
              <ProjectTypeSelect
                className="w-full"
                {...field}
                onChange={createProjectTypeHandler(field)}
                projectDependsOn={projectType?.project_depends_on}
              />
            )}
          />
          <Field
            name="invoicing_company_id"
            label={t("projects.fields.invoicing_company")}
            required={isRequired(projectSchema.shape.invoicing_company_id)}
            render={({ field }) => <InvoicingCompanySelect {...field} />}
          />
          <Field
            name="start_date"
            label={t("projects.fields.start_date")}
            required={isRequired(projectSchema.shape.start_date)}
            render={({ field }) => (
              <DatePicker
                className="w-full"
                {...field}
                datePickerProps={{ startMonth: MIN_PROJECT_DATE }}
              />
            )}
          />
          <Field
            name="end_date"
            label={t("projects.fields.end_date")}
            required={isRequired(projectSchema.shape.end_date)}
            render={({ field }) => (
              <DatePicker
                className="w-full"
                {...field}
                datePickerProps={{ startMonth: MIN_PROJECT_DATE }}
              />
            )}
          />
          <Field
            name="services_and_goods_id"
            label={t("projects.fields.services_and_goods")}
            required={isRequired(projectSchema.shape.services_and_goods_id)}
            render={({ field }) => (
              <ServicesAndGoodsSelect
                className="w-full"
                {...field}
                defaultItem={{
                  id: field.value,
                  name: servicesAndGoods.services_and_goods,
                  code: servicesAndGoods.services_and_goods_code,
                }}
              />
            )}
          />
          <Field
            name="crs_number"
            label={t("projects.fields.crs_number")}
            required={isRequired(projectSchema.shape.crs_number)}
            render={({ field }) => <Input className="w-full" {...field} />}
          />
          {isRoleBasedProject && (
            <Field
              name="payroll_type_id"
              label={t("projects.fields.payroll_type")}
              render={({ field }) => (
                <PayrollTypeSelect
                  {...field}
                  className="w-full"
                  enabled={isRoleBasedProject}
                  projectDependsOn={PROJECT_DEPENDENCY_ROLE}
                  allowClearSelection
                />
              )}
            />
          )}
        </Fieldset>
        <Fieldset className="mt-0" title={t("projects.title.settings")}>
          <CheckboxField
            name="geolocalization_enabled"
            label={t("projects.fields.geolocalization_enabled")}
            required={isRequired(projectSchema.shape.geolocalization_enabled)}
          />
          <CheckboxField
            name="contractor_card_required"
            label={t("projects.fields.contractor_card_required")}
            required={isRequired(projectSchema.shape.contractor_card_required)}
          />
          <ProjectPlannedDurationVisibilityFields disabled={disabled} />
          <CheckboxField
            name="contractor_visits_editable_by_pr"
            label={t("projects.fields.contractor_visits_editable_by_pr")}
            required={isRequired(
              projectSchema.shape.contractor_visits_editable_by_pr,
            )}
            disabled={disabled || !isAdmin}
          />
          <CheckboxField
            name="contractor_visits_hours_editable_by_contractor"
            label={t(
              "projects.fields.contractor_visits_hours_editable_by_contractor",
            )}
            required={isRequired(
              projectSchema.shape
                .contractor_visits_hours_editable_by_contractor,
            )}
            disabled={disabled || !isAdmin}
          />
          <CheckboxField
            name="contractor_visits_confirmable_by_contractor"
            label={t(
              "projects.fields.contractor_visits_confirmable_by_contractor",
            )}
            required={isRequired(
              projectSchema.shape.contractor_visits_confirmable_by_contractor,
            )}
            disabled={disabled || !isAdmin}
          />
        </Fieldset>
      </PageContent>
    </Form>
  );
};

export default ProjectCreatorForm;
